import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';

import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { UseAddVariablePickerModalArgs, UseAddVariablePickerModalResult } from '../types';
import { useVariablePickerOptions } from './useVariablePickerOptions';

export const useAddVariablePickerModal = ({
  projectId,
  onClose,
}: UseAddVariablePickerModalArgs): UseAddVariablePickerModalResult => {
  const intl = useIntl();
  const addSource = useAnalysisDefinitionStore((state) => state.addSource);

  const {
    allOptions,
    options,
    searchQuery,
    onSearchChange,
    isLoading,
    isError,
    emptyMessage,
    noResultsMessage,
  } = useVariablePickerOptions(projectId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const onSelect = useCallback(
    (analyticalVariableId: string) => setSelectedId(analyticalVariableId),
    []
  );

  // Stages the addition; Save persists it (matches how the cohort pick stages).
  const onConfirm = useCallback(() => {
    if (!selectedId) return;
    const selectedOption = allOptions.find((option) => option.analyticalVariableId === selectedId);
    addSource(selectedId, selectedOption?.description ?? null);
    onClose();
  }, [selectedId, allOptions, addSource, onClose]);

  const canConfirm = selectedId !== null && !isLoading;

  const dialogMessages: ApiWorkflowDialogMessages = {
    entrySaveButtonText: intl.formatMessage({ id: 'analysisDetail.addVariableModal.addConfirm' }),
    entryCancelButtonText: intl.formatMessage({ id: 'analysisDetail.addVariableModal.cancel' }),
  };

  return {
    title: intl.formatMessage({ id: 'analysisDetail.addVariableModal.addTitle' }),
    selectHeading: intl.formatMessage(
      { id: 'analysisDetail.addVariableModal.selectHeading' },
      { count: options.length }
    ),
    searchPlaceholder: intl.formatMessage({
      id: 'analysisDetail.addVariableModal.searchPlaceholder',
    }),
    emptyMessage,
    noResultsMessage,
    searchQuery,
    onSearchChange,
    options,
    selectedId,
    onSelect,
    onConfirm,
    onCancelClose: onClose,
    isLoading,
    isError,
    canConfirm,
    dialogMessages,
  };
};
